<template>
  <div
    v-stable-chat-width
    ref="messageList"
    class="agent-service-message-list"
    :class="{ 'is-mobile': mobile }"
    @scroll.passive="handleListScroll"
  >
    <!-- 返回上次阅读位置，期间有{count}条新消息 -->
    <!-- {count}条新消息 -->
    <button
      v-if="historyUnreadCount > 0 && historyAnchorMessageKey"
      type="button"
      class="history-new-message-button"
      :aria-label="
        $t('agentServiceUI.returnToReadPosition', {
          count: historyUnreadCount,
        })
      "
      @click="jumpToHistoryAnchor"
    >
      {{
        $t("agentServiceUI.newMessageCount", { count: historyUnreadCount })
      }}
    </button>
    <div ref="messageContent" data-chat-width-canvas class="chat-width-canvas">
      <!-- 加载中 -->
      <!-- 查看更早消息 -->
      <button
        v-if="hasMore"
        class="load-older-button"
        type="button"
        :disabled="loadingOlder"
        @click="$emit('load-older')"
      >
        {{
          loadingOlder
            ? $t("agentServiceUI.loading")
            : $t("agentServiceUI.viewOlderMessages")
        }}
      </button>

      <!-- 可以直接询问库存、交期、MOQ或商品信息 -->
      <div
        v-if="messages.length === 0 && !agentProcessing && !loading"
        class="empty-message"
      >
        {{ emptyText || $t("agentServiceUI.orderEmpty") }}
      </div>

      <div
        v-for="message in messages"
        :key="message.businessMessageId"
        :data-message-key="messageKey(message)"
      >
        <div v-if="shouldShowTimeDivider(message)" class="message-time-divider">
          {{ formatTimeDivider(message.createdAt) }}
        </div>
        <div class="message-row" :class="messageClass(message)">
          <div class="message-content">
            <div v-if="message.actorType === 'AI_AGENT' && answerDuration(message) !== null" class="answer-timing">
              {{ $t('agentServiceUI.answerElapsed', { duration: formatAnswerDuration(answerDuration(message)) }) }}
            </div>
            <!-- 供需专用货物卡片；普通商品、订单和总客服气泡保持原样 -->
            <SupplyDemandGoodsCard v-if="message.messageType === 'SUPPLY_DEMAND_CARD'" :mobile="mobile" :message="message"
              @interact="pauseForSupplyDemandCard" />
            <div v-else class="bubble-line">
              <div class="message-bubble">
                <div
                  v-if="shouldShowOriginal(message) && message.actorType !== 'SYSTEM'"
                  class="message-text message-markdown"
                  dir="auto"
                  v-html="renderMessageMarkdown(messageText(message), message)"
                ></div>
                <span
                  v-else-if="shouldShowOriginal(message)"
                  class="message-text"
                  dir="auto"
                  >{{ messageText(message) }}</span
                >
                <div
                  v-if="shouldShowTranslation(message)"
                  class="message-translation"
                  :class="{ 'is-single': translationMode === 'single' }"
                  role="status"
                >
                  <div
                    v-if="translationFor(message).status === 'SUCCEEDED'"
                    class="translation-text message-markdown"
                    :lang="translationFor(message).snapshot.targetLanguage"
                    dir="auto"
                    v-html="renderMessageMarkdown(translatedPresentation(message).content)"
                  ></div>
                  <!-- 翻译中 -->
                  <!-- 译文暂不可用 -->
                  <span v-else class="translation-state">{{
                    translationFor(message).status === "PENDING"
                      ? $t("agentServiceUI.translating")
                      : $t("agentServiceUI.translationUnavailable")
                  }}</span>
                </div>
              </div>
            </div>
            <div v-if="message.actorType === 'AI_AGENT' && message.messageType !== 'SUPPLY_DEMAND_CARD'" class="answer-actions">
              <button type="button" class="answer-action"
                :title="$t(copiedMessageId === messageKey(message) ? 'agentServiceUI.answerCopied' : 'agentServiceUI.copyAnswer')"
                :aria-label="$t(copiedMessageId === messageKey(message) ? 'agentServiceUI.answerCopied' : 'agentServiceUI.copyAnswer')"
                :disabled="isAnswerTyping(message) || !answerCopyText(message)"
                @click="copyAnswer(message)">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
                  <path v-if="copiedMessageId === messageKey(message)" d="m5 12 4 4L19 6"
                    stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" />
                  <template v-else>
                    <rect x="8" y="7" width="10" height="12" rx="2" stroke="currentColor" stroke-width="1.9" />
                    <path d="M6 16.5H5.5A2.5 2.5 0 0 1 3 14V5.5A2.5 2.5 0 0 1 5.5 3H13A2.5 2.5 0 0 1 15.5 5.5V5.8"
                      stroke="currentColor" stroke-width="1.9" stroke-linecap="round" />
                  </template>
                </svg>
              </button>
              <span class="message-ai-mark" :aria-label="$t('agentServiceUI.aiReply')">AI</span>
              <span v-if="copiedMessageId === messageKey(message) || copyErrorMessageId === messageKey(message)"
                class="answer-copy-status" role="status">{{ $t(copyErrorMessageId === messageKey(message)
                  ? 'agentServiceUI.answerCopyFailed' : 'agentServiceUI.answerCopied') }}</span>
            </div>
            <div
              v-if="messageSuggestions(message).length"
              class="message-suggestions"
            >
              <button
                v-for="question in messageSuggestions(message)"
                :key="question"
                :title="question"
                type="button"
                :disabled="!canSelectSuggestion"
                @click="selectSuggestion(message, question)"
              >
                {{ question }}
              </button>
            </div>
            <div v-if="message.localStatus" class="message-meta">
              <!-- 发送中 -->
              <span v-if="message.localStatus === 'SENDING'">{{
                $t("agentServiceUI.sending")
              }}</span>
              <!-- 发送失败，点击重试 -->
              <button
                v-if="message.localStatus === 'FAILED'"
                type="button"
                class="retry-button"
                @click="$emit('retry', message)"
              >
                {{ $t("agentServiceUI.sendFailedRetry") }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- {name}正在回复 -->
      <div
        v-if="agentProcessing"
        class="message-row is-thinking"
        :class="messageClass({ actorType: 'AI_AGENT' })"
        role="status"
        aria-live="polite"
        :aria-label="
          $t('agentServiceUI.aiResponding', { name: aiDisplayName })
        "
      >
        <div class="message-content">
          <div class="bubble-line">
            <div class="message-bubble thinking-bubble" aria-hidden="true">
              <svg viewBox="0 0 44 16" focusable="false">
                <circle cx="8" cy="8" r="3"></circle>
                <circle cx="22" cy="8" r="3"></circle>
                <circle cx="36" cy="8" r="3"></circle>
              </svg>
            </div>
            <span class="message-ai-mark" aria-hidden="true">AI</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import stableChatWidth from "@/services/chat-stable-width";
import { splitAgentServiceSuggestions } from "@/services/agent-service-contract";
import { renderAgentServiceMarkdown } from "@/services/agent-service-markdown";
import { responseTimingScope, rememberResponseStart, responseDuration, responseSource, completeResponseTiming } from "@/services/agent-service-response-timing";
import SupplyDemandGoodsCard from "./supply-demand/SupplyDemandGoodsCard.vue";
import { playbackScope, rememberInterruptedPlayback, shouldReplayInterruptedMessage, completeInterruptedPlayback } from "@/services/agent-service-playback";

export default {
  name: "AgentServiceMessageList",
  directives: { stableChatWidth },
  components: { SupplyDemandGoodsCard },
  props: {
    mobile: { type: Boolean, default: false },
    active: { type: Boolean, default: true },
    preserveReadPosition: { type: Boolean, default: false },
    emptyText: {
      type: String,
      default: "",
    },
    messages: { type: Array, default: () => [] },
    translations: { type: Object, default: () => ({}) },
    languagePreference: { type: Object, default: null },
    translationMode: { type: String, default: "bilingual" },
    conversation: { type: Object, default: null },
    staffName: { type: String, default: "" },
    currentUserId: { type: String, default: "" },
    viewerRole: { type: String, default: "" },
    agentProcessing: { type: Boolean, default: false },
    hasMore: { type: Boolean, default: false },
    loadingOlder: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    canSelectSuggestion: { type: Boolean, default: false },
    // 打开会话前由收件箱游标计算，列表据此保留“上次离开”阅读锚点。
    newMessageCount: { type: Number, default: 0 },
  },
  data() {
    return {
      displayedMessageText: {},
      knownMessageIds: {},
      typingTimerIds: {},
      typingScope: '',
      awaitingInitialSnapshot: false,
      highestKnownSequence: 0,
      followLatest: true,
      inactiveReadPosition: null,
      historyUnreadCount: 0,
      historyAnchorMessageKey: "",
      answerDurations: {},
      answerPresentationEpoch: 0,
      copiedMessageId: "",
      copyErrorMessageId: "",
    };
  },
  computed: {
    aiDisplayName() {
      return this.resolveAiDisplayName();
    },
  },
  watch: {
    /** 每次打开均展示最新记录，等浮窗及消息完成布局后再定位。 */
    active(value) {
      if (!value) this.suspendTypewriter();
      else this.resumeInterruptedTypewriters();
      if (this.preserveReadPosition) {
        if (!value) this.captureReadPosition();
        else this.$nextTick(this.restoreReadPosition);
        return;
      }
      if (value) {
        this.followLatest = true;
        this.$nextTick(this.scrollToLatest);
      }
    },
    translationMode() {
      this.$nextTick(this.handleLayoutResize);
    },
    /** AI逐字展示会继续增高；只跟随原本位于底部的读者，不打断历史阅读。 */
    displayedMessageText: {
      handler() {
        const element = this.$refs.messageList;
        if (
          element &&
          element.scrollHeight - element.clientHeight - element.scrollTop <= 24
        )
          this.$nextTick(this.scrollToBottom);
      },
      deep: true,
    },
    /** 译文补齐不追加消息/未读；正在读历史时保留位置，位于底部时跟随增高。 */
    translations() {
      this.messages.forEach(this.finishAnswerPresentation);
      const element = this.$refs.messageList;
      if (!element) return;
      const previousTop = element.scrollTop;
      const atBottom =
        element.scrollHeight - element.clientHeight - previousTop <= 24;
      this.$nextTick(() => {
        if (atBottom) this.scrollToBottom();
        else element.scrollTop = previousTop;
      });
    },
    messages: {
      handler(messages) {
        this.handleMessagesChanged(messages);
        this.$nextTick(this.prepareHistoryAnchor);
      },
      deep: false,
    },
    newMessageCount: {
      immediate: true,
      handler(value) {
        this.historyUnreadCount = Math.max(0, Number(value || 0));
        this.$nextTick(this.prepareHistoryAnchor);
      },
    },
    conversation: {
      handler(nextConversation, previousConversation) {
        if (
          nextConversation?.conversationId ===
          previousConversation?.conversationId
        ) {
          return;
        }
        this.suspendTypewriter();
        this.resetMessageLifecycle();
        this.typingScope = playbackScope(this.currentUserId, nextConversation?.conversationId);
        this.inactiveReadPosition = null;
        this.awaitingInitialSnapshot = true;
        this.historyUnreadCount = Math.max(
          0,
          Number(this.newMessageCount || 0)
        );
        this.historyAnchorMessageKey = "";
        this.$nextTick(this.scrollToLatest);
      },
      deep: false,
    },
  },
  mounted() {
    this.typingScope = playbackScope(this.currentUserId, this.conversation?.conversationId);
    // nextTick只覆盖当前渲染；译文、字体和浮窗尺寸变化需要布局观察继续校正。
    if (typeof ResizeObserver !== "undefined") {
      this.layoutObserver = new ResizeObserver(this.handleLayoutResize);
      if (this.$refs.messageContent)
        this.layoutObserver.observe(this.$refs.messageContent);
      if (this.$refs.messageList)
        this.layoutObserver.observe(this.$refs.messageList);
    }
    this.initializeMessageSnapshot(this.messages);
    // 列表可能先挂载、历史再返回；空列表不能提前消耗首次快照定位。
    this.awaitingInitialSnapshot = this.messages.length === 0;
    this.prepareHistoryAnchor();
    this.$nextTick(this.scrollToLatest);
  },
  beforeDestroy() {
    this.answerPresentationEpoch++;
    clearTimeout(this.copyFeedbackTimer);
    this.suspendTypewriter();
    this.layoutObserver?.disconnect();
    if (this.scrollFrame != null && typeof window !== "undefined")
      window.cancelAnimationFrame?.(this.scrollFrame);
    if (this.scrollSettleFrame != null && typeof window !== "undefined")
      window.cancelAnimationFrame?.(this.scrollSettleFrame);
    clearTimeout(this.scrollSettleTimer);
    this.clearTypingTimers();
  },
  methods: {
    /** 新回答从完整语法树逐步显字；历史与译文直接排版，不修改落库原文或推荐问题。 */
    renderMessageMarkdown(content, message) {
      if (message?.actorType === "AI_AGENT" && this.typingTimerIds[this.messageKey(message)]) {
        const full = this.messagePresentation(message).content;
        return renderAgentServiceMarkdown(full, Array.from(content).length / Math.max(1, Array.from(full).length));
      }
      return renderAgentServiceMarkdown(content);
    },
    /** 只记录本人发送中的客户端起点，正式历史快照、接待方和其他账号不补造计时。 */
    captureResponseStarts(messages) {
      const scope = responseTimingScope(this.currentUserId, this.conversation);
      messages.forEach(message => {
        if (String(message.actorUserId) === String(this.currentUserId)
          && message.conversationId === this.conversation?.conversationId)
          rememberResponseStart(scope, message);
      });
    },
    answerDuration(message) {
      return this.answerDurations[this.messageKey(message)]
        ?? responseDuration(responseTimingScope(this.currentUserId, this.conversation), this.messageKey(message));
    },
    formatAnswerDuration(milliseconds) {
      const seconds = Math.max(0, Math.round(milliseconds / 1000));
      return seconds < 60
        ? this.$t("agentServiceUI.answerSeconds", { seconds })
        : this.$t("agentServiceUI.answerMinutes", { minutes: Math.floor(seconds / 60), seconds: seconds % 60 });
    },
    /** 单语只显示译文时，隐藏原文的打字计时器不阻止已经完整可见的回答结束计时或复制。 */
    isAnswerTyping(message) {
      return Boolean(this.typingTimerIds[this.messageKey(message)] && this.shouldShowOriginal(message));
    },
    /** 最后一段文字提交到DOM后的显示帧结束计时；异步回调必须仍属于当前账号、会话和播放周期。 */
    finishAnswerPresentation(message) {
      const id = this.messageKey(message);
      if (message.actorType !== "AI_AGENT" || !this.active || this.isAnswerTyping(message) || this.answerDuration(message) !== null
        || (!this.shouldShowOriginal(message) && this.translationFor(message)?.status !== "SUCCEEDED")) return;
      const scope = responseTimingScope(this.currentUserId, this.conversation);
      const epoch = this.answerPresentationEpoch;
      this.$nextTick(() => {
        const finish = () => {
          if (!this.active || epoch !== this.answerPresentationEpoch || this.isAnswerTyping(message)
            || scope !== responseTimingScope(this.currentUserId, this.conversation)) return;
          const duration = completeResponseTiming(scope, responseSource(this.messages, message), id);
          if (duration !== null) this.$set(this.answerDurations, id, duration);
        };
        if (typeof window !== "undefined" && window.requestAnimationFrame) window.requestAnimationFrame(finish);
        else finish();
      });
    },
    /** 复制当前可见语言的完整回答，不把推荐问题、计时及AI标记混入正文。 */
    answerCopyText(message) {
      const parts = [];
      if (this.shouldShowOriginal(message)) parts.push(this.messagePresentation(message).content);
      if (this.shouldShowTranslation(message) && this.translationFor(message)?.status === "SUCCEEDED")
        parts.push(this.translatedPresentation(message).content);
      return parts.filter(Boolean).join("\n\n");
    },
    /** 与myAi使用相同的复制交互；Clipboard不可用或被拒绝时降级，并恢复焦点、清理临时元素。 */
    async copyAnswer(message) {
      const id = this.messageKey(message), scope = responseTimingScope(this.currentUserId, this.conversation);
      const epoch = this.answerPresentationEpoch;
      const content = this.answerCopyText(message);
      if (!content || this.isAnswerTyping(message)) return;
      let copied = false;
      this.copiedMessageId = this.copyErrorMessageId = "";
      try {
        if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(content);
          copied = true;
        }
      } catch (_) { /* 浏览器拒绝Clipboard时继续使用页面内复制降级。 */ }
      if (!copied && typeof document !== "undefined") {
        const textarea = document.createElement("textarea"), focused = document.activeElement;
        textarea.value = content;
        textarea.readOnly = true;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        try {
          document.body.appendChild(textarea);
          textarea.select();
          copied = document.execCommand("copy") === true;
        } catch (_) { copied = false; }
        finally { textarea.remove(); focused?.focus?.({ preventScroll: true }); }
      }
      if (epoch !== this.answerPresentationEpoch || scope !== responseTimingScope(this.currentUserId, this.conversation)) return;
      this.copiedMessageId = copied ? id : "";
      this.copyErrorMessageId = copied ? "" : id;
      clearTimeout(this.copyFeedbackTimer);
      this.copyFeedbackTimer = setTimeout(() => { this.copiedMessageId = this.copyErrorMessageId = ""; }, 2000);
    },
    /** 隐藏或销毁时只暂停本地打字，已落库原文不变；保存的仅是待重播消息ID。 */
    suspendTypewriter() {
      this.answerPresentationEpoch++;
      const scope = this.typingScope || playbackScope(this.currentUserId, this.conversation?.conversationId);
      for (const id of Object.keys(this.typingTimerIds)) rememberInterruptedPlayback(scope, id);
      this.clearTypingTimers();
    },
    resumeInterruptedTypewriters() {
      for (const message of this.messages || []) {
        if (message.actorType === 'AI_AGENT' && shouldReplayInterruptedMessage(this.typingScope, this.messageKey(message)) && !this.typingTimerIds[this.messageKey(message)]) this.startTypewriter(message);
        else if (!this.typingTimerIds[this.messageKey(message)]) this.finishAnswerPresentation(message);
      }
    },
    /** 用户展开供需资料时暂停自动追到末尾；普通消息和用户主动滚回末尾仍沿用原策略。 */
    pauseForSupplyDemandCard() {
      this.followLatest = false;
      clearTimeout(this.scrollSettleTimer);
      for (const key of ['scrollFrame', 'scrollSettleFrame']) {
        if (this[key] != null && typeof window !== 'undefined') window.cancelAnimationFrame?.(this[key]);
        this[key] = null;
      }
    },
    captureReadPosition() {
      const element = this.$refs.messageList;
      if (element?.clientHeight > 0) this.inactiveReadPosition = { top: element.scrollTop, followLatest: this.followLatest };
    },
    restoreReadPosition() {
      const element = this.$refs.messageList;
      if (!this.active || !element || !this.inactiveReadPosition) return;
      this.followLatest = this.inactiveReadPosition.followLatest;
      element.scrollTop = this.inactiveReadPosition.top;
    },
    /** 以进入会话前的未读数反推最后已读消息；默认仍展示最新消息。 */
    prepareHistoryAnchor() {
      const stableMessages = (this.messages || []).filter(
        (message) => this.messageSequence(message) > 0
      );
      const unread = Math.min(this.historyUnreadCount, stableMessages.length);
      if (!unread || !stableMessages.length) {
        this.historyAnchorMessageKey = "";
        return;
      }
      const anchorIndex = Math.max(0, stableMessages.length - unread - 1);
      this.historyAnchorMessageKey = this.messageKey(
        stableMessages[anchorIndex]
      );
    },
    /** 使用浏览器原生平滑滚动回到上次阅读锚点，不启动逐帧脚本动画。 */
    jumpToHistoryAnchor() {
      const content = this.$refs.messageContent;
      if (!content || !this.historyAnchorMessageKey) return;
      const target = Array.from(
        content.querySelectorAll("[data-message-key]")
      ).find(
        (element) => element.dataset.messageKey === this.historyAnchorMessageKey
      );
      if (!target) return;
      this.followLatest = false;
      target.scrollIntoView({
        behavior: this.prefersReducedMotion() ? "auto" : "smooth",
        block: "center",
      });
      this.historyUnreadCount = 0;
      this.$emit("history-anchor-visited");
    },
    /** 记录读者是否主动离开末尾；隐藏窗口的零尺寸不能覆盖打开时的跟随状态。 */
    handleListScroll() {
      const element = this.$refs.messageList;
      if (this.active && element?.clientHeight > 0)
        this.followLatest =
          element.scrollHeight - element.clientHeight - element.scrollTop <= 24;
    },
    /** 迟到的译文和窗口布局仅对正在查看最新消息的读者执行末尾定位。 */
    handleLayoutResize() {
      if (this.active && this.followLatest) this.scrollToBottom();
    },
    /** 中文同语窗口隐藏附文；异语展示仍按原快照，不删改历史、不触发重译。 */
    translationFor(message) {
      if (
        this.languagePreference?.customerLanguage === "zh-CN" ||
        !this.currentUserId ||
        message.actorType === "SYSTEM" ||
        message.localStatus ||
        this.isOwnMessage(message)
      )
        return null;
      const item = this.translations?.[message.businessMessageId];
      if (
        !item ||
        item.snapshot?.customerLanguage === "zh-CN" ||
        item.status === "NOT_REQUIRED" ||
        item.businessMessageId !== message.businessMessageId ||
        item.conversationId !== this.conversation?.conversationId ||
        item.tenantId !== this.conversation?.tenantId
      )
        return null;
      const target =
        message.actorType === "CUSTOMER"
          ? "zh-CN"
          : item.snapshot?.customerLanguage;
      if (item.snapshot?.targetLanguage !== target) return null;
      return item;
    },
    /**
     * 本人发出的真人消息，以及当前业务员名下的AI代聊消息，始终只展示写入业务库的原文。
     * 单双语开关只作用于“对方发来的消息”，不能把自己的话再翻译给自己看。
     */
    isOwnMessage(message) {
      if (!this.currentUserId || message.actorType === "SYSTEM") return false;
      // 总客服AI属于平台，人工负责人登录后也不能被视为本人代聊消息。
      if (message.actorType === "AI_AGENT" && this.conversation?.scene?.sceneType === "GENERAL") return false;
      if (this.conversation?.scene?.sceneType === 'GENERAL'
        && String(this.conversation.customerUserId) === String(this.currentUserId)
        && String(this.conversation.assignedStaffUserId) === String(this.currentUserId)) {
        return String(message.actorUserId) === String(this.currentUserId)
          && (this.viewerRole === 'RECEPTION' ? ['BUSINESS_STAFF', 'SUPPORT_STAFF'].includes(message.actorType) : message.actorType === 'CUSTOMER');
      }
      const ownerId =
        message.actorType === "AI_AGENT"
          ? this.conversation?.assignedStaffUserId
          : message.actorUserId;
      return Boolean(ownerId && String(ownerId) === String(this.currentUserId));
    },
    /** 单语以成功译文覆盖对方原文；等待或失败时继续隐藏原文并展示明确状态。 */
    shouldShowOriginal(message) {
      if (this.isOwnMessage(message)) return true;
      if (!this.shouldShowTranslation(message)) return true;
      return this.translationMode !== "single" || !this.translationFor(message);
    },
    /** 双语只给对方消息追加译文；内容完全相同时去重，避免同一句显示两遍。 */
    shouldShowTranslation(message) {
      const translation = this.translationFor(message);
      if (!translation) return false;
      if (translation.status !== "SUCCEEDED") return true;
      if (this.translationMode === "single") return true;
      const normalize = (value) =>
        String(value || "")
          .trim()
          .replace(/\s+/gu, " ");
      return (
        normalize(this.translatedPresentation(message).content) !==
        normalize(this.messagePresentation(message).content)
      );
    },
    resolveAiDisplayName() {
      if (this.conversation?.scene?.sceneType === "GENERAL") {
        return `🤖 ${this.conversation.assistantDisplayName || "XSOU客服助手"}`;
      }
      const name =
        String(this.conversation?.assignedStaffDisplayName || "").trim() ||
        this.staffName.trim() ||
        "业务员";
      return `🤖 ${name}`;
    },
    messageKey(message) {
      return String(
        message?.businessMessageId || message?.clientMessageId || ""
      );
    },
    messageSequence(message) {
      const sequence = Number(message?.sequence || 0);
      return Number.isSafeInteger(sequence) && sequence > 0 ? sequence : 0;
    },
    setDisplayedMessageText(messageId, content) {
      if (!messageId) return;
      if (typeof this.$set === "function") {
        this.$set(this.displayedMessageText, messageId, content);
      } else {
        this.displayedMessageText[messageId] = content;
      }
    },
    deleteTypingTimer(messageId) {
      if (typeof this.$delete === "function") {
        this.$delete(this.typingTimerIds, messageId);
      } else {
        delete this.typingTimerIds[messageId];
      }
    },
    initializeMessageSnapshot(messages = []) {
      this.captureResponseStarts(messages);
      messages.forEach((message) => {
        const messageId = this.messageKey(message);
        if (!messageId) return;
        this.knownMessageIds[messageId] = true;
        this.highestKnownSequence = Math.max(this.highestKnownSequence, this.messageSequence(message));
        if (this.active && message.actorType === 'AI_AGENT' && shouldReplayInterruptedMessage(this.typingScope, messageId)) {
          this.startTypewriter(message);
          return;
        }
        this.setDisplayedMessageText(
          messageId,
          this.messagePresentation(message).content
        );
        this.finishAnswerPresentation(message);
        this.highestKnownSequence = Math.max(
          this.highestKnownSequence,
          this.messageSequence(message)
        );
      });
      this.awaitingInitialSnapshot = false;
    },
    syncMessagePresentation(messages = []) {
      this.captureResponseStarts(messages);
      if (this.awaitingInitialSnapshot) {
        this.initializeMessageSnapshot(messages);
        return;
      }
      messages.forEach((message) => {
        const messageId = this.messageKey(message);
        if (!messageId) return;
        if (this.knownMessageIds[messageId]) {
          if (!this.typingTimerIds[messageId]) {
            this.setDisplayedMessageText(
              messageId,
              this.messagePresentation(message).content
            );
            this.finishAnswerPresentation(message);
          }
          return;
        }
        const sequence = this.messageSequence(message);
        const isEarlierHistory =
          sequence > 0 &&
          this.highestKnownSequence > 0 &&
          sequence <= this.highestKnownSequence;
        this.knownMessageIds[messageId] = true;
        this.highestKnownSequence = Math.max(
          this.highestKnownSequence,
          sequence
        );
        if (
          message.actorType === "AI_AGENT" &&
          !isEarlierHistory &&
          !this.prefersReducedMotion()
        ) {
          this.startTypewriter(message);
          return;
        }
        this.setDisplayedMessageText(
          messageId,
          this.messagePresentation(message).content
        );
        this.finishAnswerPresentation(message);
      });
    },
    handleMessagesChanged(messages = []) {
      const isInitialSnapshot = this.awaitingInitialSnapshot;
      const element = this.$refs.messageList;
      const previousScrollTop = Number(element?.scrollTop || 0);
      const previousScrollHeight = Number(element?.scrollHeight || 0);
      const addedMessages = messages.filter(
        (message) => !this.knownMessageIds[this.messageKey(message)]
      );
      const hasEarlierHistory = addedMessages.some((message) => {
        const sequence = this.messageSequence(message);
        return (
          sequence > 0 &&
          this.highestKnownSequence > 0 &&
          sequence <= this.highestKnownSequence
        );
      });
      const hasRealtimeAppend = addedMessages.some(
        (message) =>
          message.localStatus === "SENDING" ||
          this.messageSequence(message) > this.highestKnownSequence
      );

      this.syncMessagePresentation(messages);
      if (isInitialSnapshot && messages.length === 0)
        this.awaitingInitialSnapshot = true;
      this.$nextTick(() => {
        if (!this.active || this.$refs.messageList !== element) return;
        if (isInitialSnapshot || hasRealtimeAppend) {
          if (isInitialSnapshot) this.scrollToLatest();
          else this.scrollToBottom();
          return;
        }
        if (hasEarlierHistory && element) {
          const addedHeight = Math.max(
            0,
            Number(element.scrollHeight || 0) - previousScrollHeight
          );
          element.scrollTop = previousScrollTop + addedHeight;
        }
      });
    },
    startTypewriter(message) {
      const messageId = this.messageKey(message);
      if (this.typingTimerIds[messageId]) {
        clearInterval(this.typingTimerIds[messageId]);
        this.deleteTypingTimer(messageId);
      }
      const characters = Array.from(this.messagePresentation(message).content);
      if (!messageId || characters.length === 0) {
        this.setDisplayedMessageText(messageId, "");
        return;
      }
      if (!this.active) {
        rememberInterruptedPlayback(this.typingScope, messageId);
        return;
      }
      this.setDisplayedMessageText(messageId, "");
      let visibleLength = 0;
      const step = Math.max(1, Math.ceil(characters.length / 48));
      const timerId = setInterval(() => {
        if (!this.active) { this.suspendTypewriter(); return; }
        visibleLength = Math.min(characters.length, visibleLength + step);
        this.setDisplayedMessageText(
          messageId,
          characters.slice(0, visibleLength).join("")
        );
        if (visibleLength >= characters.length) {
          clearInterval(timerId);
          this.deleteTypingTimer(messageId);
          completeInterruptedPlayback(this.typingScope, messageId);
          this.finishAnswerPresentation(message);
        }
      }, 28);
      if (typeof this.$set === "function") {
        this.$set(this.typingTimerIds, messageId, timerId);
      } else {
        this.typingTimerIds[messageId] = timerId;
      }
      if (!this.shouldShowOriginal(message)) this.finishAnswerPresentation(message);
    },
    clearTypingTimers() {
      Object.values(this.typingTimerIds).forEach((timerId) =>
        clearInterval(timerId)
      );
      this.typingTimerIds = {};
    },
    resetMessageLifecycle() {
      this.answerPresentationEpoch++;
      this.answerDurations = {};
      this.copiedMessageId = this.copyErrorMessageId = "";
      clearTimeout(this.copyFeedbackTimer);
      this.clearTypingTimers();
      this.displayedMessageText = {};
      this.knownMessageIds = {};
      this.highestKnownSequence = 0;
    },
    prefersReducedMotion() {
      return Boolean(
        typeof window !== "undefined" &&
          window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
      );
    },
    messageText(message) {
      const messageId = this.messageKey(message);
      return Object.prototype.hasOwnProperty.call(
        this.displayedMessageText,
        messageId
      )
        ? this.displayedMessageText[messageId]
        : this.messagePresentation(message).content;
    },
    messagePresentation(message) {
      return splitAgentServiceSuggestions(message);
    },
    translatedPresentation(message) {
      return splitAgentServiceSuggestions(
        {
          ...message,
          content: this.translationFor(message)?.translatedContent || "",
        },
        this.messagePresentation(message).questions.length
      );
    },
    /** 打字结束后才显示推荐选项，不把展示动作当作新的业务消息。 */
    messageSuggestions(message) {
      if (this.typingTimerIds[this.messageKey(message)]) return [];
      const translation = this.translationFor(message);
      if (translation) {
        // 等待/失败时不回退成中文按钮；已保存译文按消息自身的冻结语言展示。
        if (translation.status !== "SUCCEEDED") return [];
        return this.translatedPresentation(message).questions;
      }
      return this.messagePresentation(message).questions;
    },
    selectSuggestion(message, question) {
      if (
        !this.canSelectSuggestion ||
        !this.messageSuggestions(message).includes(question)
      )
        return;
      this.$emit("select-suggestion", question);
    },
    authorName(message) {
      if (message.actorType === "AI_AGENT") {
        return this.resolveAiDisplayName();
      }
      if (["BUSINESS_STAFF", "SUPPORT_STAFF"].includes(message.actorType)) {
        // 人工回复保留当时作者快照，不能随接待配置变化改署名。
        if (this.conversation?.scene?.sceneType === "GENERAL") {
          return String(message.displayNameSnapshot || this.staffName || "业务员");
        }
        return (
          this.staffName.trim() ||
          String(message.displayNameSnapshot || "业务员")
        );
      }
      return message.displayNameSnapshot;
    },
    /** 真人按当前登录用户判断方向；AI代负责业务员发言，仅在负责业务员端靠右。 */
    messageClass(message) {
      // 货物卡片属于双方共享的资料气泡，始终靠左，不套用系统通知的居中样式。
      if (message.messageType === 'SUPPLY_DEMAND_CARD') return 'is-supply-demand-card';
      if (message.actorType === "SYSTEM") return "is-system";
      const isAi = message.actorType === "AI_AGENT";
      const actorClass = isAi
        ? "is-ai"
        : ["BUSINESS_STAFF", "SUPPORT_STAFF"].includes(message.actorType)
        ? "is-staff"
        : "is-customer";
      const isSelf = this.isOwnMessage(message);
      return `${actorClass} ${isSelf ? "is-self" : "is-peer"}`;
    },
    /** 时间只在首条、跨日期或相邻消息间隔达到五分钟时显示为居中节点。 */
    shouldShowTimeDivider(message) {
      const index = this.messages.indexOf(message);
      if (index <= 0) return true;
      const current = new Date(message.createdAt).getTime();
      const previous = new Date(this.messages[index - 1]?.createdAt).getTime();
      if (!Number.isFinite(current) || !Number.isFinite(previous)) return false;
      return (
        current - previous >= 5 * 60 * 1000 ||
        new Date(current).toDateString() !== new Date(previous).toDateString()
      );
    },
    /** 采用常见聊天工具的相对日期格式，不在每个气泡下重复时间。 */
    formatTimeDivider(value) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      const now = new Date();
      const time = date.toLocaleTimeString("zh-CN", {
        hour: "2-digit",
        minute: "2-digit",
      });
      if (date.toDateString() === now.toDateString()) return time;
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      if (date.toDateString() === yesterday.toDateString())
        return `昨天 ${time}`;
      if (now.getTime() - date.getTime() < 7 * 24 * 60 * 60 * 1000)
        return `${date.toLocaleDateString("zh-CN", {
          weekday: "long",
        })} ${time}`;
      return `${date.getMonth() + 1}月${date.getDate()}日 ${time}`;
    },
    /** 新消息确认后滚动到列表末尾。 */
    scrollToBottom() {
      const element = this.$refs.messageList;
      if (!this.active || !element) return;
      this.followLatest = true;
      element.scrollTop = element.scrollHeight;
      // 浮窗刚打开时浏览器可能尚未完成最终布局；下一帧再校正，关闭后禁止迟到滚动。
      if (typeof window !== "undefined" && window.requestAnimationFrame) {
        if (this.scrollFrame != null)
          window.cancelAnimationFrame?.(this.scrollFrame);
        this.scrollFrame = window.requestAnimationFrame(() => {
          this.scrollFrame = null;
          if (
            this.active &&
            this.followLatest &&
            this.$refs.messageList === element
          )
            element.scrollTop = element.scrollHeight;
        });
      }
    },
    /**
     * 浮窗显示后会经历内容挂载、字体排版和译文回填，多阶段校正保证首次落到最新消息。
     */
    scrollToLatest() {
      if (!this.active) return;
      this.followLatest = true;
      clearTimeout(this.scrollSettleTimer);
      if (this.scrollSettleFrame != null && typeof window !== "undefined")
        window.cancelAnimationFrame?.(this.scrollSettleFrame);
      const settle = () => {
        const element = this.$refs.messageList;
        if (!this.active || !this.followLatest || !element) return;
        element.scrollTop = element.scrollHeight;
      };
      const schedule =
        this.scheduleFrame ||
        ((callback) =>
          typeof window !== "undefined" && window.requestAnimationFrame
            ? window.requestAnimationFrame(callback)
            : setTimeout(callback, 0));
      settle();
      this.scrollSettleFrame = schedule(() => {
        settle();
        this.scrollSettleFrame = schedule(settle);
      });
      this.scrollSettleTimer = setTimeout(settle, 120);
    },
  },
};
</script>

<style lang="less" scoped>
.message-translation {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--m-border-default, #dde3ee);
  color: var(--m-text-secondary, #667085);
  &.is-single {
    margin-top: 0;
    padding-top: 0;
    border-top: 0;
    color: inherit;
  }
  .translation-text {
    display: block;
    font-size: 14px;
    line-height: 22px;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
  .translation-state {
    display: block;
    font-size: 13px;
    line-height: 20px;
  }
}
.agent-service-message-list {
  &.is-mobile {
    padding: 12px 12px 8px;
    .message-row .message-content { max-width: 88%; }
    .message-row.is-ai .message-content { max-width: 100%; }
    .message-row .message-bubble { max-width: 100%; }
    .message-row .message-suggestions { grid-template-columns: minmax(0, 1fr); }
    .load-older-button, .retry-button { min-height: 44px; }
  }
  min-width: 0;
  overflow-x: hidden;
  .chat-width-canvas { container-name: chat-messages; container-type: inline-size; }
  position: relative;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 18px 16px 10px;
  background: rgb(255, 255, 255);

  @container chat-messages (max-width: 500px) { .message-row .message-content .message-suggestions { grid-template-columns: minmax(0, 1fr); } }
  @container chat-messages (min-width: 900px) { .message-row .message-content .message-suggestions { grid-template-columns: repeat(4, minmax(0, 1fr)); } }
  .history-new-message-button {
    position: sticky;
    z-index: 4;
    top: 10px;
    display: block;
    margin: 0 0 -34px auto;
    min-height: 32px;
    padding: 0 14px;
    border: 1px solid #dbe8fb;
    border-radius: 16px;
    color: #246de0;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 4px 14px rgba(36, 109, 224, 0.12);
    font-size: 12px;
    line-height: 30px;
    cursor: pointer;
    transition: background-color 180ms ease, box-shadow 180ms ease;

    &:hover,
    &:focus-visible {
      background: #f2f7ff;
      box-shadow: 0 6px 18px rgba(36, 109, 224, 0.18);
    }

    &:focus-visible {
      outline: 2px solid #4286f5;
      outline-offset: 2px;
    }
  }

  .message-time-divider {
    margin: 18px 0 14px;
    color: #98a2b3;
    font-size: 12px;
    line-height: 18px;
    text-align: center;
  }

  .load-older-button {
    display: block;
    margin: 0 auto 18px;
    border: 0;
    background: transparent;
    color: #718078;
    cursor: pointer;
  }

  .empty-message {
    margin: 80px auto;
    color: #8a938e;
    text-align: center;
  }

  .message-row {
    display: flex;
    align-items: flex-start;
    margin-bottom: 16px;

    .message-content {
      display: flex;
      max-width: 78%;
      min-width: 0;
      flex-direction: column;
      align-items: flex-start;
    }

    .bubble-line {
      display: flex;
      max-width: 100%;
      align-items: flex-end;
      gap: 6px;
    }

    .message-bubble {
      max-width: 100%;
      padding: 10px 14px;
      border: 0;
      border-radius: var(--chat-radius, 12px);
      background: rgb(246, 246, 247);
      color: #333;
      font-size: 14px;
      line-height: 1.55;
      white-space: pre-wrap;
      word-break: break-word;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
    }

    .message-markdown {
      white-space: normal;
      overflow-wrap: anywhere;

      ::v-deep p {
        margin: 0 0 8px;
      }

      ::v-deep > :last-child {
        margin-bottom: 0;
      }

      ::v-deep h1,
      ::v-deep h2,
      ::v-deep h3,
      ::v-deep h4,
      ::v-deep h5,
      ::v-deep h6 {
        margin: 12px 0 8px;
        font-size: 16px;
        font-weight: 600;
        line-height: 1.5;
      }

      ::v-deep > :first-child {
        margin-top: 0;
      }

      ::v-deep ul,
      ::v-deep ol {
        margin: 8px 0;
        padding-inline-start: 1.5em;
      }

      ::v-deep li {
        margin: 4px 0;
      }

      ::v-deep blockquote {
        margin: 8px 0;
        padding-inline-start: 12px;
        border-inline-start: 3px solid #dbe3ee;
        color: #667085;
      }

      ::v-deep code {
        padding: 1px 4px;
        border-radius: 4px;
        background: rgba(15, 23, 42, 0.06);
        font-size: 0.9em;
        white-space: pre-wrap;
      }

      ::v-deep pre {
        max-width: 100%;
        margin: 8px 0;
        padding: 12px;
        overflow-x: auto;
        border-radius: 4px;
        background: rgba(15, 23, 42, 0.06);
        white-space: pre;
      }

      ::v-deep pre code {
        padding: 0;
        background: transparent;
        white-space: inherit;
      }

      ::v-deep a {
        color: #246de0;
        text-decoration: underline;
      }

      ::v-deep .chat-markdown-table {
        max-width: 100%;
        margin: 8px 0;
        overflow-x: auto;
      }

      ::v-deep table {
        border-collapse: collapse;
        font-size: 13px;
      }

      ::v-deep th,
      ::v-deep td {
        padding: 6px 8px;
        border: 1px solid #dbe3ee;
        text-align: start;
      }
    }

    .message-ai-mark {
      flex: 0 0 auto;
      padding: 0 3px 1px;
      color: #7f8792;
      font-size: 11px;
      font-weight: 600;
      line-height: 16px;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }

    .answer-timing {
      align-self: stretch;
      margin-bottom: 12px;
      padding-bottom: 8px;
      border-bottom: 1px solid var(--m-divider, #eef2f7);
      color: var(--m-text-secondary, #667085);
      font-size: 12px;
      line-height: 20px;
      font-variant-numeric: tabular-nums;
    }

    .answer-actions {
      display: flex;
      align-items: center;
      gap: 4px;
      margin-top: 8px;
      color: var(--m-text-secondary, #667085);

      .answer-action {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        padding: 4px;
        border: 0;
        border-radius: var(--chat-radius, 12px);
        background: transparent;
        color: inherit;
        cursor: pointer;

        svg { width: 20px; height: 20px; }
        &:hover:not(:disabled) { background: var(--m-surface-muted, #f8fafc); }
        &:focus-visible { outline: 2px solid #4286f5; outline-offset: 2px; }
        &:disabled { color: #98a2b3; cursor: default; }
      }

      .answer-copy-status { font-size: 12px; line-height: 20px; }

      @media (max-width: 630px), (pointer: coarse) {
        .answer-action { width: 44px; height: 44px; }
      }
    }

    .message-meta {
      display: flex;
      gap: 8px;
      margin: 5px 4px 0;
      color: #9aa19d;
      font-size: 12px;
    }

    .message-suggestions {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      width: 100%;
      min-width: 0;
      box-sizing: border-box;
      align-self: stretch;
      justify-content: start;
      gap: 8px;
      max-width: 100%;
      margin-top: 8px;

      button {
        width: 100%;
        min-width: 0;
        box-sizing: border-box;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
        min-height: 36px;
        padding: 4px 12px;
        border: 1px solid #dde3ee;
        border-radius: var(--chat-radius, 12px);
        background: #fff;
        color: #246de0;
        font: inherit;
        font-size: 13px;
        line-height: 20px;
        text-align: left;
        cursor: pointer;
        transition: background-color 200ms, border-color 200ms, color 200ms;

        &:hover:not(:disabled),
        &:focus-visible {
          border-color: #c7dcfd;
          background: #f2f7ff;
          outline: 2px solid #4286f5;
          outline-offset: 2px;
        }

        &:active:not(:disabled) {
          color: #1e5bb8;
        }
        &:disabled {
          background: #f8fafc;
          color: #667085;
          cursor: default;
        }
      }

      @media (max-width: 630px) {
        button {
          min-height: 44px;
          padding: 8px 16px;
        }
      }
      @media (prefers-reduced-motion: reduce) {
        button {
          transition: none;
        }
      }
    }

    .retry-button {
      border: 0;
      background: transparent;
      color: #c2463a;
      cursor: pointer;
    }

    &.is-self {
      flex-direction: row-reverse;

      .message-content {
        align-items: flex-end;
      }
      .bubble-line {
        flex-direction: row-reverse;
      }
      .message-suggestions {
        justify-content: flex-end;
      }
      &:not(.is-ai) .message-bubble {
        background: rgb(201, 231, 255);
        color: #1f2937;
      }
    }

    &.is-ai {
      .message-content {
        width: 100%;
        max-width: 100%;
        align-items: flex-start;
      }

      .bubble-line { width: 100%; flex-direction: column; align-items: flex-start; }

      .message-bubble {
        width: 100%;
        min-width: 0;
        box-sizing: border-box;
        padding: 0;
        border: 0;
        border-radius: 0;
        background: transparent;
        box-shadow: none;
        color: var(--m-text-primary, #334155);
        line-height: 1.8;
        white-space: normal;

        .message-markdown {
          ::v-deep table { width: 100%; }
          ::v-deep th,
          ::v-deep td {
            padding: 8px 12px;
            border: 0;
            border-bottom: 1px solid var(--m-divider, #eef2f7);
            vertical-align: top;
          }
          ::v-deep th { border-bottom-color: var(--m-border-default, #dde3ee); }
          ::v-deep hr { margin: 16px 0; border: 0; border-top: 1px solid var(--m-divider, #eef2f7); }
        }
      }

      .thinking-bubble {
        display: flex;
        align-items: center;
        min-width: 58px;
        min-height: 36px;
        padding: 8px 12px;

        svg {
          width: 44px;
          height: 16px;
          fill: #667085;

          circle {
            animation: agent-service-thinking 1.2s ease-in-out infinite;

            &:nth-child(2) {
              animation-delay: 0.16s;
            }

            &:nth-child(3) {
              animation-delay: 0.32s;
            }
          }
        }
      }
    }

    &.is-supply-demand-card {
      justify-content: flex-start;
      .message-content { width: 100%; max-width: 100%; align-items: flex-start; }
    }

    &.is-system {
      justify-content: center;
      align-items: center;

      .message-bubble {
        padding: 6px 12px;
        border: 0;
        border-radius: 4px;
        background: #dedfdf;
        color: #68716c;
        font-size: 12px;
      }
    }
  }
}

@keyframes agent-service-thinking {
  0%,
  60%,
  100% {
    opacity: 0.35;
    transform: translateY(0);
  }

  30% {
    opacity: 1;
    transform: translateY(-3px);
  }
}

@media (max-width: 600px) {
  .agent-service-message-list {
    padding: 16px 14px 10px;

    .message-row .message-bubble {
      max-width: 100%;
    }

    .message-row .message-content {
      max-width: 78%;
    }

    .message-row.is-ai .message-content { max-width: 100%; }

    .load-older-button,
    .retry-button {
      min-height: 44px;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .agent-service-message-list {
    .history-new-message-button {
      transition: none;
    }

    .message-row.is-ai .thinking-bubble svg circle {
      animation: none;
    }
  }
}
</style>
